import { QueryClient } from 'react-query';
import { getContext, put, select, takeLatest } from 'redux-saga/effects';
import { ProjectId, TaskId } from 'api/models';
import { Project } from 'api/project';
import { TaskAPI } from 'api/tasks';
import {
  formatDate,
  getDueDateFromGantt,
  getGanttTaskId,
  getPhaseList,
  getTaskList,
} from 'components/organisms/projects/project-plan/timeline/helpers';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { Task } from 'data/tasks';
import {
  createDependencySagaAction,
  CreateDependencyActionPayload,
  dependencyCreatedAction,
} from '../actions';
import { selectTask, selectTasksByProjectId } from '../selectors';
import { updateTasksDates } from '../slice';

function* createDependencySaga(
  action: StoreActionProps<CreateDependencyActionPayload>,
): Generator {
  const { payload } = action;

  if (payload) {
    const { taskId, projectId, dependentTaskId } = payload;

    yield TaskAPI.createDependentTask(projectId, taskId, {
      dependentTaskIds: [dependentTaskId],
    });
  }
}

function* handleEmptyTaskDates(
  action: StoreActionProps<
    CreateDependencyActionPayload & {
      canAssignDates: boolean;
    }
  >,
): Generator {
  const { payload } = action;

  if (!payload) {
    return;
  }
  const { taskId, projectId, dependentTaskId, canAssignDates } = payload;

  if (!canAssignDates) {
    return;
  }

  const task = (yield select(selectTask(TaskId(projectId, taskId)))) as Task;
  const dependentTask = (yield select(
    selectTask(TaskId(projectId, dependentTaskId)),
  )) as Task;

  if (
    task.startDate &&
    task.dueDate &&
    dependentTask.startDate &&
    dependentTask.dueDate
  ) {
    return;
  }

  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));

  if (!project) {
    return;
  }

  const { projectPhases } = project;
  const tasks = (yield select(
    selectTasksByProjectId(ProjectId(projectId)),
  )) as Task[];

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);

  const _task = taskList[taskId];
  const _dependentTask = taskList[dependentTaskId];
  const ganttRef = (yield getContext('ganttRef')) as any;

  const { taskStore } = ganttRef?.instance;

  // TOOD: fix type for updates.
  const taskUpdates: any[] = [];

  if (!_dependentTask.hasStartDate || !_dependentTask.hasDueDate) {
    const ganttId = getGanttTaskId(dependentTaskId);
    const record = taskStore.getById(ganttId);

    record.set('isCalculatedStartDate', false);
    record.set('isCalculatedEndDate', false);

    yield TaskAPI.updateTask(projectId, dependentTaskId, {
      startDate: _dependentTask.startDate,
      dueDate: _dependentTask.dueDate,
    });

    taskUpdates.push({
      id: dependentTaskId,
      startDate: _dependentTask.startDate,
      dueDate: _dependentTask.dueDate,
    });
  }

  if (!_task.hasStartDate || !_task.hasDueDate) {
    const ganttId = getGanttTaskId(taskId);
    const record = taskStore.getById(ganttId);

    record.set('isCalculatedStartDate', false);
    record.set('isCalculatedEndDate', false);

    yield TaskAPI.updateTask(projectId, taskId, {
      startDate: formatDate(record.getStartDate()),
      dueDate: getDueDateFromGantt(record),
    });

    taskUpdates.push({
      id: taskId,
      startDate: formatDate(record.getStartDate()),
      dueDate: getDueDateFromGantt(record),
    });
  }

  if (taskUpdates.length > 0) {
    yield put(updateTasksDates(taskUpdates));
  }
}

export function* watchCreateDependencySaga(): Generator {
  yield takeLatest(createDependencySagaAction.type, createDependencySaga);
  yield takeLatest(createDependencySagaAction.type, handleEmptyTaskDates);
  yield takeLatest(dependencyCreatedAction.type, handleEmptyTaskDates);
}
